import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    Table,
    Input,
    Text,
    Badge,
    Flex,
    Button,
    Switch,
} from '@chakra-ui/react';
import {
    DialogBody,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogRoot,
    DialogTitle,
    DialogBackdrop
} from './ui/dialog';
import {toaster} from "./ui/toaster.jsx";
import { Field } from './ui/field.jsx';
import { closeAllPositions, getPositions, getTradeHistory, updateStopLossPrice } from '../api';
import { HiRefresh } from 'react-icons/hi';
import StopLossMonitors from './StopLossMonitors';
import PageHeader from './PageHeader';
import FilterBar from './FilterBar';

const Positions = () => {
    const [positions, setPositions] = useState([]);
    const [selectedPosition, setSelectedPosition] = useState(null);
    const [tradeHistory, setTradeHistory] = useState([]);
    const [filters, setFilters] = useState({
        symbol: '',
        status: 'OPEN',
        showAllDates: false,
        strategy: '',
    });
    const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [editingPosition, setEditingPosition] = useState(null);
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
    const [newStopLossPrice, setNewStopLossPrice] = useState('');
    const [isCloseAllDialogOpen, setIsCloseAllDialogOpen] = useState(false);
    const [isClosingAll, setIsClosingAll] = useState(false);

    // 从选择组件返回的复杂对象中提取简单值的通用函数
    const extractValueFromSelection = (selectionValue) => {
        // 如果为空，返回空字符串
        if (!selectionValue) return '';
        
        // 如果是简单字符串，直接返回
        if (typeof selectionValue === 'string') return selectionValue;
        
        // 如果是有value属性的对象
        if (selectionValue.value !== undefined) {
            // 如果value是数组，取第一个元素
            return Array.isArray(selectionValue.value) 
                ? selectionValue.value[0] 
                : selectionValue.value;
        }
        
        // 如果是数组，取第一个元素
        if (Array.isArray(selectionValue)) return selectionValue[0];
        
        // 不符合上述任何格式，返回空字符串
        return '';
    };

    const fetchPositions = async () => {
        try {
            const now = new Date();
            const etDate = filters.showAllDates ? '' : new Date(now.toLocaleString('en-US', { timeZone: 'America/New_York' }));

            // 使用通用函数提取值
            const statusValue = extractValueFromSelection(filters.status);
            const strategyValue = extractValueFromSelection(filters.strategy);
            
            // 明确构建简单的参数对象
            const params = {
                symbol: filters.symbol || '',
                status: statusValue,
                strategy: strategyValue,
                showAllDates: filters.showAllDates,
                date: etDate ? etDate.toISOString() : '',
            };

            console.log('发送请求参数:', params);

            // 直接传递参数对象
            const response = await getPositions(params);

            const data = response.positions ? response.positions : response;
            setPositions(data);
        } catch (error) {
            console.error('获取持仓失败:', error);
        }
    };

    const fetchTradeHistory = async (positionId) => {
        try {
            const response = await getTradeHistory(positionId);
            console.log('Fetched trade history:', response); // 添加调试日志
            
            // 处理可能的嵌套数据结构
            const data = response.history ? response.history : response;
            return data;
        } catch (error) {
            console.error('获取交易历史失败:', error);
            toaster.create({
                title: "获取交易历史失败",
                description: error.message,
                type: 'error',
            });
            return null;
        }
    };

    useEffect(() => {
        fetchPositions();
    }, [filters]);

    const handleRowClick = async (position) => {
        try {
            console.log('点击行:', position);
            // 1. 设置选中的持仓
            setSelectedPosition(position);
            // 2. 清空之前的交易历史
            setTradeHistory([]);
            // 3. 设置加载状态
            setIsLoading(true);
            
            // 4. 获取交易历史数据
            console.log(`正在获取持仓 ID:${position.id} 的交易历史...`);
            const data = await fetchTradeHistory(position.id);
            
            // 5. 设置交易历史数据
            if (data && Array.isArray(data)) {
                setTradeHistory(data);
                console.log('交易历史数据加载成功:', data.length, '条记录');
            } else {
                console.log('没有获取到交易历史数据或数据格式不正确');
            }
            
            // 6. 直接设置对话框状态为打开
            console.log('准备打开对话框');
            setDetailsDialogOpen(true);
            
        } catch (error) {
            console.error('处理行点击事件失败:', error);
            toaster.create({
                title: "操作失败",
                description: "无法显示交易历史，请稍后再试",
                type: 'error',
            });
        } finally {
            setIsLoading(false);
        }
    };

    const getPositionTypeBadge = (type) => {
        const colors = {
            'CALL': 'green',
            'PUT': 'red',
        };
        const text = {
            'CALL': '看涨',
            'PUT': '看跌',
        };
        return (
            <Badge colorPalette={colors[type]}>
                {text[type]}
            </Badge>
        );
    };

    const getStatusBadge = (status) => {
        const colors = {
            'OPEN': 'green',
            'PARTIAL_CLOSED': 'yellow',
            'CLOSED': 'red',
        };
        const text = {
            'OPEN': '开仓',
            'PARTIAL_CLOSED': '部分平仓',
            'CLOSED': '平仓',
        };
        return (
            <Badge colorPalette={colors[status]}>
                {text[status]}
            </Badge>
        );
    };

    const getTradeTypeBadge = (type) => {
        const typeColors = {
            'ENTRY': 'green',
            'PARTIAL_EXIT': 'yellow',
            'FULL_EXIT': 'red',
        };
        const typeText = {
            'ENTRY': '开仓',
            'PARTIAL_EXIT': '部分平仓',
            'FULL_EXIT': '全部平仓',
        };
        return (
            <Badge colorPalette={typeColors[type] || 'gray'}>
                {typeText[type] || type}
            </Badge>
        );
    };

    // 计算盈亏百分比
    const calculatePnL = (position) => {
        if (!position.current_price || !position.entry_price) return null;
        const pnl = ((position.current_price - position.entry_price) / position.entry_price) * 100;
        return pnl.toFixed(2);
    };

    const formatDateTime = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleString('zh-CN');
    };

    const handleRefresh = async () => {
        setIsLoading(true);
        try {
            await fetchPositions();
            toaster.create({
                title: "刷新成功",
                type: 'success',
            });
        } catch (error) {
            toaster.create({
                title: "刷新失败",
                description: error.message,
                type: 'error',
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleCloseAllPositions = async () => {
        setIsClosingAll(true);
        try {
            const result = await closeAllPositions();
            const closedCount = result?.closed?.length || 0;
            const failedCount = result?.failed?.length || 0;

            if (failedCount > 0) {
                toaster.create({
                    title: '部分清仓失败',
                    description: `成功 ${closedCount}，失败 ${failedCount}`,
                    type: 'warning',
                });
            } else {
                toaster.create({
                    title: '清仓完成',
                    description: `已清仓 ${closedCount} 个持仓`,
                    type: 'success',
                });
            }

            await fetchPositions();
            setIsCloseAllDialogOpen(false);
        } catch (error) {
            const message = error.response?.data?.detail || error.message || '请稍后重试';
            toaster.create({
                title: '清仓失败',
                description: message,
                type: 'error',
            });
        } finally {
            setIsClosingAll(false);
        }
    };

    const calculateTotalPnL = () => {
        console.log('Positions:', positions); // 添加调试日志
        const total = positions.reduce((total, position) => {
            console.log('Position:', position.symbol, 'PnL:', position.total_profit_loss); // 添加调试日志
            const pnl = parseFloat(position.total_profit_loss) || 0;
            return total + pnl;
        }, 0);
        console.log('Total PnL:', total); // 添加调试日志
        return Number(total) || 0;
    };

    // 打开编辑止损价的对话框
    const handleEditStopLoss = (position, e) => {
        // 阻止事件冒泡，避免触发行点击事件
        e.stopPropagation();
        setEditingPosition(position);
        setNewStopLossPrice(position.stop_loss_price);
        setIsEditDialogOpen(true);
    };

    // 保存止损价
    const saveStopLossPrice = async () => {
        try {
            console.log('保存新止损价:', newStopLossPrice, '持仓ID:', editingPosition.id);
            
            // 调用API更新止损价
            await updateStopLossPrice(editingPosition.id, newStopLossPrice);
            
            toaster.create({
                title: '修改成功',
                description: `${editingPosition.symbol} 止损价已修改为 ${newStopLossPrice}`,
                type: 'success',
            });
            
            // 刷新持仓数据
            await fetchPositions();
            
            // 关闭对话框
            setIsEditDialogOpen(false);
        } catch (error) {
            console.error('修改止损价失败:', error);
            
            // 提取详细的错误信息
            const errorMessage = error.response?.data?.detail || error.message || '请稍后重试';
            
            toaster.create({
                title: '修改失败',
                description: errorMessage,
                type: 'error',
            });
        }
    };

    // 在组件返回前添加一个调试信息
    console.log('渲染组件, detailsDialogOpen 状态:', detailsDialogOpen);

    return (
        <Box>
            <PageHeader
                title="持仓概览"
                description="关注当前持仓与风险状态"
                actions={
                    <Flex gap={2} wrap="wrap">
                        <Button leftIcon={<HiRefresh />} onClick={handleRefresh} isLoading={isLoading} size="md">
                            刷新
                        </Button>
                        <Button colorPalette="red" onClick={() => setIsCloseAllDialogOpen(true)} size="md">
                            一键清仓
                        </Button>
                    </Flex>
                }
            />

            <FilterBar
                left={
                    <>
                        <Field label="显示所有日期" display="flex">
                            <Switch.Root
                                checked={filters.showAllDates}
                                onCheckedChange={({ checked }) =>
                                    setFilters({ ...filters, showAllDates: checked })
                                }
                            >
                                <Switch.HiddenInput />
                                <Switch.Control>
                                    <Switch.Thumb />
                                </Switch.Control>
                            </Switch.Root>
                        </Field>
                        <Input
                            placeholder="搜索标的"
                            value={filters.symbol}
                            onChange={(e) => setFilters({ ...filters, symbol: e.target.value })}
                            maxW="200px"
                        />
                    </>
                }
                right={
                    <Text fontWeight="bold">
                        总盈亏：
                        <Text as="span" color={calculateTotalPnL() >= 0 ? 'green.500' : 'red.500'}>
                            ${calculateTotalPnL().toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </Text>
                    </Text>
                }
                summary="点击持仓可查看详细交易历史"
            />

            <Card.Root>
                <Card.Body>
                    <Box overflowX="auto">
                        <Table.Root size="sm" interactive>
                            <Table.Header>
                                <Table.Row>
                                    <Table.ColumnHeader>标的</Table.ColumnHeader>
                                    <Table.ColumnHeader isNumeric>数量</Table.ColumnHeader>
                                    <Table.ColumnHeader isNumeric>入场价</Table.ColumnHeader>
                                    <Table.ColumnHeader isNumeric>现价</Table.ColumnHeader>
                                    <Table.ColumnHeader isNumeric>止损价</Table.ColumnHeader>
                                    <Table.ColumnHeader whiteSpace="nowrap">入场时间</Table.ColumnHeader>
                                    <Table.ColumnHeader whiteSpace="nowrap">更新时间</Table.ColumnHeader>
                                    <Table.ColumnHeader>状态</Table.ColumnHeader>
                                    <Table.ColumnHeader isNumeric>剩余</Table.ColumnHeader>
                                    <Table.ColumnHeader isNumeric>盈亏</Table.ColumnHeader>
                                    <Table.ColumnHeader>操作</Table.ColumnHeader>
                                </Table.Row>
                            </Table.Header>
                            <Table.Body>
                                {positions.map((position) => (
                                    <Table.Row key={position.id} _hover={{ bg: 'gray.50' }} cursor="pointer">
                                        <Table.Cell py={2}>{position.symbol}</Table.Cell>
                                        <Table.Cell py={2} isNumeric>{position.quantity}</Table.Cell>
                                        <Table.Cell py={2} isNumeric>{position.entry_price}</Table.Cell>
                                        <Table.Cell py={2} isNumeric>{position.current_price}</Table.Cell>
                                        <Table.Cell py={2} isNumeric>{position.stop_loss_price}</Table.Cell>
                                        <Table.Cell py={2} whiteSpace="nowrap">{formatDateTime(position.entry_time)}</Table.Cell>
                                        <Table.Cell py={2} whiteSpace="nowrap">{formatDateTime(position.last_updated)}</Table.Cell>
                                        <Table.Cell py={2}>{getStatusBadge(position.status)}</Table.Cell>
                                        <Table.Cell py={2} isNumeric>{position.remaining_quantity}</Table.Cell>
                                        <Table.Cell py={2} isNumeric>
                                            <Text color={position.total_profit_loss >= 0 ? 'green.500' : 'red.500'}>
                                                {position.total_profit_loss ? `$${position.total_profit_loss}` : '-'}
                                            </Text>
                                        </Table.Cell>
                                        <Table.Cell py={2}>
                                            <Flex gap={2}>
                                                <Button
                                                    size="xs"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleRowClick(position);
                                                    }}
                                                >
                                                    详情
                                                </Button>
                                                <Button
                                                    size="xs"
                                                    onClick={(e) => handleEditStopLoss(position, e)}
                                                    isDisabled={position.status === 'CLOSED'}
                                                >
                                                    改止损
                                                </Button>
                                            </Flex>
                                        </Table.Cell>
                                    </Table.Row>
                                ))}
                            </Table.Body>
                        </Table.Root>
                    </Box>
                </Card.Body>
            </Card.Root>

            <Card.Root mt={8}>
                <Card.Header>
                    <Text fontSize="md" fontWeight="bold">止损监控</Text>
                </Card.Header>
                <Card.Body>
                    <StopLossMonitors hiddenTitle={true} />
                </Card.Body>
            </Card.Root>

            <DialogRoot 
                open={detailsDialogOpen} 
                onOpenChange={(value) => {
                    console.log('详情Dialog onOpenChange 触发, 接收值:', value);
                    
                    // 任何关闭信号都直接关闭对话框
                    if (value === false || (value && value.open === false)) {
                        console.log('关闭交易历史对话框');
                        setDetailsDialogOpen(false);
                    }
                }} 
                size="6xl"
            >
                {console.log('渲染详情对话框, 状态:', detailsDialogOpen)}
                <DialogBackdrop onClick={() => {
                    console.log('点击背景，直接关闭对话框');
                    setDetailsDialogOpen(false);
                }} />
                <DialogContent maxW="90vw" onClick={e => e.stopPropagation()}>
                    <DialogHeader>
                        {selectedPosition && (
                          <Flex direction="column">
                              <DialogTitle>交易历史 - {selectedPosition?.symbol}</DialogTitle>
                              {selectedPosition?.strategy && (
                                <Text fontSize="sm" color="gray.500">
                                    策略: {selectedPosition?.strategy}
                                </Text>
                              )}
                              {selectedPosition?.signal_reason && (
                                  <Text fontSize="sm" color="gray.500">
                                      信号原因: {selectedPosition?.signal_reason}
                                  </Text>
                              )}
                          </Flex>
                        )}
                    </DialogHeader>
                    <DialogBody pb={6}>
                        {isLoading ? (
                            <Flex justify="center" align="center" py={10}>
                                <Text>加载中...</Text>
                            </Flex>
                        ) : tradeHistory.length > 0 ? (
                            <Box overflowX="auto">
                                <Table.Root size="sm" interactive>
                                    <Table.Header>
                                        <Table.Row>
                                            <Table.ColumnHeader>时间</Table.ColumnHeader>
                                            <Table.ColumnHeader>类型</Table.ColumnHeader>
                                            <Table.ColumnHeader>数量</Table.ColumnHeader>
                                            <Table.ColumnHeader>价格</Table.ColumnHeader>
                                            <Table.ColumnHeader>盈亏</Table.ColumnHeader>
                                            <Table.ColumnHeader minW="200px">原因</Table.ColumnHeader>
                                            <Table.ColumnHeader minW="300px">详细说明</Table.ColumnHeader>
                                        </Table.Row>
                                    </Table.Header>
                                    <Table.Body>
                                        {tradeHistory.map((trade) => (
                                            <Table.Row key={trade.id}>
                                                <Table.Cell whiteSpace="nowrap">{formatDateTime(trade.trade_time)}</Table.Cell>
                                                <Table.Cell>{getTradeTypeBadge(trade.trade_type)}</Table.Cell>
                                                <Table.Cell isNumeric>{trade.quantity}</Table.Cell>
                                                <Table.Cell isNumeric>{trade.entry_price || trade.exit_price}</Table.Cell>
                                                <Table.Cell isNumeric>
                                                    {trade.profit_loss !== null && trade.profit_loss !== undefined ? (
                                                        <Text color={Number(trade.profit_loss) >= 0 ? "green.500" : "red.500"}>
                                                            ${Number(trade.profit_loss).toFixed(2)}
                                                        </Text>
                                                    ) : '-'}
                                                </Table.Cell>
                                                <Table.Cell>{trade.reason}</Table.Cell>
                                                <Table.Cell>{trade.reason_detail}</Table.Cell>
                                            </Table.Row>
                                        ))}
                                    </Table.Body>
                                </Table.Root>
                            </Box>
                        ) : (
                            <Flex justify="center" align="center" py={10}>
                                <Text>暂无交易历史数据</Text>
                            </Flex>
                        )}
                    </DialogBody>
                    <DialogFooter>
                        <Button 
                            variant="outline" 
                            onClick={() => setDetailsDialogOpen(false)}
                        >
                            关闭
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </DialogRoot>

            <DialogRoot 
                open={isEditDialogOpen} 
                onOpenChange={(value) => {
                    // 简化关闭逻辑
                    if (value === false || (value && value.open === false)) {
                        setIsEditDialogOpen(false);
                    }
                }}
            >
                <DialogBackdrop />
                <DialogContent onClick={e => e.stopPropagation()}>
                    <DialogHeader>
                        <DialogTitle>修改止损价 - {editingPosition?.symbol}</DialogTitle>
                    </DialogHeader>
                    <DialogBody pb={6}>
                        <Box mb={4}>
                            <Text fontWeight="bold" mb={2}>当前止损价</Text>
                            <Text>{editingPosition?.stop_loss_price}</Text>
                        </Box>
                        <Box>
                            <Text fontWeight="bold" mb={2}>新止损价</Text>
                            <Input 
                                value={newStopLossPrice} 
                                onChange={(e) => setNewStopLossPrice(e.target.value)}
                                type="number"
                                step="0.01"
                            />
                        </Box>
                    </DialogBody>
                    <DialogFooter>
                        <Button 
                            variant="ghost" 
                            onClick={() => setIsEditDialogOpen(false)}
                        >
                            取消
                        </Button>
                        <Button 
                            mr={3} 
                            onClick={saveStopLossPrice}
                        >
                            保存
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </DialogRoot>

            <DialogRoot
                open={isCloseAllDialogOpen}
                onOpenChange={(value) => {
                    if (value === false || (value && value.open === false)) {
                        setIsCloseAllDialogOpen(false);
                    }
                }}
            >
                <DialogBackdrop />
                <DialogContent onClick={e => e.stopPropagation()}>
                    <DialogHeader>
                        <DialogTitle>确认一键清仓</DialogTitle>
                    </DialogHeader>
                    <DialogBody pb={6}>
                        <Text>此操作将按市价清仓所有持仓，确认继续吗？</Text>
                    </DialogBody>
                    <DialogFooter>
                        <Button
                            variant="ghost"
                            onClick={() => setIsCloseAllDialogOpen(false)}
                            isDisabled={isClosingAll}
                        >
                            取消
                        </Button>
                        <Button
                            colorPalette="red"
                            onClick={handleCloseAllPositions}
                            isLoading={isClosingAll}
                        >
                            确认清仓
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </DialogRoot>
        </Box>
    );
};

export default Positions; 
